import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Radar } from "lucide-react";

export default function ControlTowerLogin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === null) return null;
  if (user && user.role === "admin") return <Navigate to="/control" replace />;

  const finish = (u) => {
    if (u.role === "admin") navigate("/control");
    else { setError("Owner access only."); logout(); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(form.email, form.password);
      if (u?.mfa_required) setMfaToken(u.mfa_token);
      else finish(u);
    } catch (err) { setError(fmtErr(err)); }
    setBusy(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
      finish(data);
    } catch (err) { setError(fmtErr(err)); }
    setBusy(false);
  };

  const inputCls = "cr-input";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="control-tower-login">
      <Seo title="Control Tower | CourierRadar" noindex />
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <span className="w-7 h-7 rounded-md bg-black flex items-center justify-center"><Radar className="w-4 h-4 text-white" /></span>
          <span className="font-extrabold text-base tracking-tight">Control Tower</span>
        </div>
        {!mfaToken ? (
          <form onSubmit={submit} className="space-y-3">
            <input data-testid="ct-email" type="email" required placeholder="Owner email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="ct-password" type="password" required placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            {error && <p data-testid="ct-error" className="text-xs text-red-600">{error}</p>}
            <button data-testid="ct-login-btn" disabled={busy}
              className="w-full bg-black text-white hover:bg-zinc-800 font-medium transition-colors px-5 py-3 rounded-lg text-sm disabled:opacity-40">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button type="button" data-testid="ct-forgot" onClick={async () => {
              if (!form.email) return setError("Enter your email first");
              await api.post("/auth/forgot-password", { email: form.email }).catch(() => {});
              toast.success("If the account exists, a reset link is on its way.");
            }} className="w-full text-center text-xs text-zinc-400 hover:text-black transition-colors">Forgot password?</button>
          </form>
        ) : (
          <form onSubmit={submitMfa} className="space-y-3">
            <p className="text-sm text-zinc-600">Enter the 6-digit code from your authenticator app.</p>
            <input data-testid="ct-mfa-code" inputMode="numeric" maxLength={6} required placeholder="000000"
              value={code} onChange={(e) => setCode(e.target.value)} className={`${inputCls} text-center text-lg tracking-[0.4em] font-mono`} />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button data-testid="ct-mfa-submit" disabled={busy}
              className="w-full bg-black text-white hover:bg-zinc-800 font-medium transition-colors px-5 py-3 rounded-lg text-sm disabled:opacity-40">Verify</button>
          </form>
        )}
        <p className="text-[11px] text-zinc-400 text-center mt-6">Private area. All actions are audited.</p>
      </div>
    </div>
  );
}
